/**
 * Runtime config — 全 repo 唯一讀取點(機制 A 地基)
 * =============================================================================
 * 把「設定值從哪來」這件事收斂到這一個檔。app 其他地方一律 `import { config } from
 * '@/config'` 讀 `config.*`,**不**直接碰 `window.__APP_CONFIG__` /
 * `import.meta.env.VITE_*`(後者由 ESLint rule `hai/no-raw-import-meta-env` 強制,
 * 白名單只放本檔)。
 *
 * ## 為什麼要 runtime config(不是 build-time env)
 * 既有作法把值在 `vite build` 時烤進 bundle(`import.meta.env.VITE_*`),代表 dev /
 * prod 要各 build 一次、值寫死在 dist 裡。機制 A 改成「**一份 image,多環境**」:
 * nginx 啟動時用 envsubst 產生外部 `/env-config.js`,在 app bundle 之前載入,把值掛上
 * `window.__APP_CONFIG__`。換環境只換 nginx 注入的環境變數,不重 build。
 *
 * ## 讀取優先序(每個 key 都走同一條鏈)
 *   1. `window.__APP_CONFIG__?.[k]`  ← runtime(nginx envsubst /env-config.js);**prod 走這條**
 *   2. dev-only fallback(本地 `pnpm dev` 無 nginx → window 是 undefined 時用)
 *   3. 空值('' 或對應預設)
 *
 * ## 本地 dev 策略(關鍵 — 兼顧「prod bundle 零 baked」)
 * 本地 `pnpm dev` 沒有 nginx,所以 `window.__APP_CONFIG__` 不存在。dev 來源走
 * **`import.meta.env.DEV` gated** 的兩段:
 *   - 個人覆寫:`import.meta.env.VITE_*`(從 gitignored `.env.local` 來;Vite `loadEnv`
 *     原生讀)。沒設就用 ↓
 *   - dev-only 公開預設:Firebase 已知公開值(security review 確認非 secret)+ dev 旗標。
 *
 * 這兩段**整段包在 `if (import.meta.env.DEV)` 內**。Vite/Rollup 在 prod build
 * (`import.meta.env.DEV` 靜態替換成 `false`)會把整個 dead branch tree-shake 掉 →
 * **dist bundle 不含任何 `AIza` 開頭的 key**(見檔尾 `readDevFallback` 註解 + tree-shake
 * 保證說明)。prod 唯一來源是 runtime `window.__APP_CONFIG__`。
 *
 * ## 必填 key 缺值 → loud(不 silent)
 * `firebaseApiKey` / `firebaseProjectId` 解析後仍空 → boot `console.error` 一次
 * (對齊 `services/index.ts` 既有 red-warn 模式)。把「nginx 漏注入 / .env.local 沒設」
 * 從 silent 失敗變成開 F12 第一眼就看到。
 */

/**
 * nginx envsubst 產生的 `/env-config.js` 掛上 `window.__APP_CONFIG__` 的形狀。
 * 值**皆字串**(envsubst 只會吐字串);bool 正規化在本檔做(`=== 'true'`)。
 * 全部 optional — 任何一條漏注入都要能 graceful 退讓(必填 key 缺值另走 red-warn)。
 */
export interface AppConfigWindow {
  readonly FIREBASE_API_KEY?: string;
  readonly FIREBASE_AUTH_DOMAIN?: string;
  readonly FIREBASE_PROJECT_ID?: string;
  /** Firestore 具名 database id;空 → (default) database(dev)。beta/prod 指各自具名 DB。 */
  readonly FIREBASE_DATABASE_ID?: string;
  /** 'dev' | 'prod'(其他值一律當非 dev 處理)。 */
  readonly APP_ENV?: string;
  /** 'true' | 'false'(字串)。 */
  readonly DEBUG?: string;
  /** API base;通常空字串(走相對路徑 + nginx / vite proxy same-origin)。 */
  readonly API_BASE_URL?: string;
  /** 這份 build 的識別 = image tag = commit short sha(R249 診斷回報用)。 */
  readonly APP_VERSION?: string;
}

/** 正規化後的 config 物件形狀(app 端讀這個)。 */
export interface AppConfig {
  readonly firebaseApiKey: string;
  readonly firebaseAuthDomain: string;
  readonly firebaseProjectId: string;
  /** Firestore 具名 database id;空字串 → (default) database。 */
  readonly firebaseDatabaseId: string;
  /** 'dev' | 'prod'。 */
  readonly appEnv: 'dev' | 'prod';
  /** debug log 總開關(已正規化成 bool)。 */
  readonly debug: boolean;
  /** API base;空字串代表走相對路徑。 */
  readonly apiBaseUrl: string;
  /**
   * 這份 build 的識別(R249 回報系統)。部署環境 = commit short sha(同時是 image tag);
   * 本地 dev = `'dev'`;**nginx 漏注入時是空字串**。
   *
   * 為什麼需要它:回報單少了這一欄,後端拿到「畫面壞掉」卻不知道對方跑的是哪份程式碼 ——
   * R225 那隻「分頁裡的主 bundle 是舊的、lazy chunk 404」就是這一類,當時要靠三次量測
   * 才定位。一個 sha 就能先分流「是不是舊 build」。
   */
  readonly appVersion: string;
}

/** Firebase 已知公開值(security review 確認非 secret;僅 dev fallback 用)。 */
const DEV_FIREBASE_API_KEY = 'AIzaSyBtE1iRN7A_2107N2HwdDtt-RrjmdKCByI';
const DEV_FIREBASE_AUTH_DOMAIN = 'hai-creative-system.firebaseapp.com';
const DEV_FIREBASE_PROJECT_ID = 'hai-creative-system';
const DEV_FIREBASE_DATABASE_ID = 'creative-dev-firestore';

/**
 * 讀 runtime 注入(`window.__APP_CONFIG__`)。SSR / 極早期(window 未就緒)安全回 undefined。
 */
function readWindowConfig(): AppConfigWindow | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__APP_CONFIG__;
}

/**
 * dev-only fallback:`import.meta.env.VITE_*`(個人 `.env.local`)→ dev 公開預設。
 *
 * **本函式所有內容只在 `import.meta.env.DEV === true` 時被呼叫**(見 `resolve()`)。
 * prod build 時 `import.meta.env.DEV` 靜態為 `false` → 呼叫點 dead → 整段(含
 * `AIza...` literal)被 tree-shake。
 *
 * 注意:`import.meta.env.VITE_*` 仍由 Vite 在 build 時做靜態替換,但因為**唯一引用點**
 * 落在 dead branch,prod bundle 不會保留這些字串。本檔是全 repo 唯一允許出現
 * `import.meta.env.VITE_*` / `import.meta.env.DEV` 的 app-config 讀取點。
 */
function readDevFallback(): AppConfigWindow {
  return {
    FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY || DEV_FIREBASE_API_KEY,
    FIREBASE_AUTH_DOMAIN:
      import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || DEV_FIREBASE_AUTH_DOMAIN,
    FIREBASE_PROJECT_ID:
      import.meta.env.VITE_FIREBASE_PROJECT_ID || DEV_FIREBASE_PROJECT_ID,
    // R131:dev 切具名 creative-dev-firestore(比照 beta/prod,(default) 退役;
    // 須與 BE api-service 的 FIRESTORE_DATABASE 同顆,否則 realtime 互看不到)。
    // 要臨時打別顆 DB 用 .env.local 的 VITE_FIREBASE_DATABASE_ID 蓋。
    FIREBASE_DATABASE_ID:
      import.meta.env.VITE_FIREBASE_DATABASE_ID || DEV_FIREBASE_DATABASE_ID,
    APP_ENV: import.meta.env.VITE_APP_ENV || 'dev',
    APP_VERSION: import.meta.env.VITE_APP_VERSION || 'dev',
    DEBUG: import.meta.env.VITE_DEBUG || 'false',
    API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
  };
}

/** envsubst 字串 → bool。只有字面 "true"(case-insensitive)為真,其餘(含空 / undefined)為假。 */
function toBool(v: string | undefined): boolean {
  return (v ?? '').trim().toLowerCase() === 'true';
}

/** 'dev' | 'prod' 收斂:非 'dev' 字面一律當 'prod'(對外保守:預設關 dev-only 行為)。 */
function toAppEnv(v: string | undefined): 'dev' | 'prod' {
  return (v ?? '').trim().toLowerCase() === 'dev' ? 'dev' : 'prod';
}

function resolve(): AppConfig {
  const win = readWindowConfig();
  // dev fallback 只在 dev build 求值(prod 時整個 readDevFallback 呼叫被 tree-shake)。
  const fb: AppConfigWindow = import.meta.env.DEV ? readDevFallback() : {};

  // 每個 key:runtime(window)優先 → dev fallback → 空。
  const pick = (k: keyof AppConfigWindow): string | undefined => win?.[k] ?? fb[k];

  const firebaseApiKey = pick('FIREBASE_API_KEY') ?? '';
  const firebaseAuthDomain = pick('FIREBASE_AUTH_DOMAIN') ?? '';
  const firebaseProjectId = pick('FIREBASE_PROJECT_ID') ?? '';

  // 必填 key 缺值 → loud(對齊 services/index.ts red-warn)。dev fallback 一定補得到,
  // 所以這條基本只在 prod 漏注入 /env-config.js 時觸發 → 開 F12 第一眼可見。
  if (!firebaseApiKey || !firebaseProjectId) {
    console.error(
      '%c[config] ⚠️ missing required runtime config (firebaseApiKey / firebaseProjectId). ' +
        'verify nginx envsubst injected /env-config.js (window.__APP_CONFIG__).',
      // eslint-disable-next-line hai/no-hardcoded-colors -- console %c 樣式字串,無 DOM 上下文、CSS var 不解析
      'color:#ef4444;font-weight:bold',
    );
  }

  return {
    firebaseApiKey,
    firebaseAuthDomain,
    firebaseProjectId,
    firebaseDatabaseId: pick('FIREBASE_DATABASE_ID') ?? '',
    appEnv: toAppEnv(pick('APP_ENV')),
    debug: toBool(pick('DEBUG')),
    apiBaseUrl: pick('API_BASE_URL') ?? '',
    appVersion: pick('APP_VERSION') ?? '',
  };
}

/**
 * 正規化後的 runtime config。module load 時 resolve 一次(值在一個 page session 內不變;
 * runtime 注入發生在 app bundle 之前,window 已就緒)。
 */
export const config: AppConfig = resolve();
